import type { Bounds, CoordinateTransformer, PointLike } from '~types';

/**
 * Create a bidirectional coordinate transformer that normalizes geographic
 * coordinates to SVG space (origin at top-left, Y-axis flipped).
 *
 * Subtracts minLon from X and flips Y relative to maxLat so all SVG coordinates
 * remain small — preventing D3's zoom transform from overshooting due to large
 * geographic offsets.
 */
const createCoordinateTransformer = (bounds: Bounds): CoordinateTransformer => ({
  toSVG: (lon, lat) => ({ x: lon - bounds.minLon, y: bounds.maxLat - lat }),
  fromSVG: (x, y) => ({ lon: x + bounds.minLon, lat: bounds.maxLat - y }),
});

/**
 * Transform an array of geographic points into SVG coordinates.
 * @param points - Array of points with lon/lat
 * @param transformer - Coordinate transformer
 * @returns Points enriched with x/y SVG coordinates
 */
const transformPointsToSvgCoords = <T extends PointLike>(
  points: T[],
  transformer: CoordinateTransformer,
): Array<T & { x: number; y: number }> => {
  return points.map((point) => ({
    ...point,
    ...transformer.toSVG(point.lon, point.lat),
  }));
};

export { createCoordinateTransformer, transformPointsToSvgCoords };
